import PropTypes from 'prop-types';
import React from 'react';

import CartItemPrice from 'Component/CartItemPrice';
import Field from 'Component/Field';
import FIELD_TYPE from 'Component/Field/Field.config';
import Link from 'Component/Link';
import { PRODUCT_CONTENANCE_ATTRIBUTE_CODE } from 'Component/ProductActions/ProductActions.config';
import ProductAttributeValue from 'Component/ProductAttributeValue';
import ProductVariation from 'Component/ProductVariation';
import ProductWishlistButton from 'Component/ProductWishlistButton';
import {
    CartItem as SourceCartItem
} from 'SourceComponent/CartItem/CartItem.component';
import { ADD_TO_WISHLIST } from 'Util/Product';
import { magentoProductTransform } from 'Util/Product/Transform';
import { VALIDATION_INPUT_TYPE } from 'Util/Validator/Config';

import './CartItem.override.style';

/** @namespace PeggysagePwa/Component/CartItem/Component */
export class CartItemComponent extends SourceCartItem {
    static propTypes = {
        ...SourceCartItem.propTypes,
        onProductAddedToWishlist: PropTypes.func.isRequired,
        handleChangeProductVariation: PropTypes.func.isRequired,
        isCollectInStoreEnabled: PropTypes.bool.isRequired,
        isChosenGiftProduct: PropTypes.bool.isRequired,
        isCheckout: PropTypes.bool
    };

    static defaultProps = {
        ...SourceCartItem.defaultProps,
        isCheckout: false
    };

    renderProductContenance() {
        const { item: { product } } = this.props;

        const attribute = product?.attributes?.[PRODUCT_CONTENANCE_ATTRIBUTE_CODE];

        if (!attribute) {
            return null;
        }

        return (
            <div block="CartItem" elem="ProductContenance">
                <ProductAttributeValue
                  attribute={ attribute }
                  isFormattedAsText
                />
            </div>
        );
    }

    renderProductSku() {
        const { item: { product: { sku } } } = this.props;

        return <span block="CartItem" elem="ProductSku">{ __('SKU: %s', sku) }</span>;
    }

    renderProductSkuAndContenance() {
        return (
            <div block="CartItem" elem="ProductSkuAndContenance">
                { this.renderProductContenance() }
                { this.renderProductSku() }
            </div>
        );
    }

    renderProductVariationUpdateLink() {
        const { isCartOverlay, isCheckout, handleChangeProductVariation } = this.props;

        if (isCartOverlay || isCheckout) {
            return null;
        }

        return (
            <button
              type="button"
              block="CartItem"
              elem="ProductVariationUpdate"
              onClick={ handleChangeProductVariation }
            >
                { __('Modify') }
            </button>
        );
    }

    renderProductVariation() {
        const {
            item: {
                product,
                product: {
                    id,
                    sku,
                    name,
                    stock_status,
                    url_key,
                    variation_thumbnail_url,
                    variation_products = []
                },
                isFreeproduct
            }
        } = this.props;

        if (!variation_products.length || isFreeproduct) {
            return null;
        }

        const variation = {
            id,
            sku,
            name,
            stock_status,
            url_key,
            variation_thumbnail_url
        };

        return (
            <div
              block="CartItem"
              elem="ProductVariation"
            >
                <ProductVariation
                  product={ product }
                  variation={ variation }
                  isInCart
                />
                { this.renderProductVariationUpdateLink() }
            </div>
        );
    }

    /**
     * Override: reorganize display
     * @returns {JSX.Element}
     */
    renderDesktopSummary() {
        return (
            <div block="CartItem" elem="Wrapper" mods={ { isSummary: true } }>
                { this.renderImage() }
                <div block="CartItem" elem="CartItemRows">
                    <div block="CartItem" elem="ProductInfo">
                        { this.renderTitle() }
                    </div>
                    <div block="CartItem" elem="ProductActions">
                        { this.renderQuantity() }
                        { this.renderProductUnitPrice() }
                    </div>
                </div>
            </div>
        );
    }

    /**
     * Override: add link to product on product name
     * @returns {JSX.Element}
     */
    renderProductName() {
        const {
            item: {
                isFreeproduct,
                product: {
                    name,
                    url
                }
            }
        } = this.props;

        if (isFreeproduct) {
            return (
                <p block="CartItem" elem="Heading">
                    { name }
                </p>
            );
        }

        return (
            <p block="CartItem" elem="Heading">
                <Link
                  block="CartItem"
                  elem="HeadingLink"
                  to={ url }
                >
                    { name }
                </Link>
            </p>
        );
    }

    /**
     * Override: add sku and contenance
     * @returns {JSX.Element}
     */
    renderTitle() {
        const { isMobileLayout } = this.props;

        const {
            item: {
                customizable_options,
                bundle_options,
                downloadable_links
            } = {}
        } = this.props;

        return (
            <div block="CartItem" elem="Title" mods={ { isMobileLayout } }>
                { this.renderProductName() }
                { this.renderProductSkuAndContenance() }
                { this.renderProductVariation() }
                { this.renderOutOfStockMessage() }
                { this.renderProductConfigurations() }
                { this.renderProductOptions(customizable_options) }
                { this.renderProductBundleOptions(bundle_options) }
                { this.renderProductLinks(downloadable_links) }
            </div>
        );
    }

    renderProductPickUpInfo() {
        const {
            isCollectInStoreEnabled,
            item: {
                product: {
                    pas_retrait_magasin
                }
            } = {}
        } = this.props;

        if (!isCollectInStoreEnabled || !pas_retrait_magasin) {
            return null;
        }

        return (
            <div block="CartItem" elem="ProductPickUpInfo">
                { __('L’option retrait en magasin Peggy Sage n’est pas disponible pour ce produit') }
            </div>
        );
    }

    /**
     * Override: remove link on mobile
     * @returns {JSX.Element|*}
     */
    renderImage() {
        return this.renderImageElement();
    }

    renderFreeProductLabel() {
        const { item: { quantity } = {} } = this.props;

        const label = quantity > 1 ? __('Offerts') : __('Offert');

        return <span>{ label }</span>;
    }

    /**
     * Override: hide price for free products
     * @returns {JSX.Element|*}
     */
    renderProductPrice() {
        const { item: { isFreeproduct } = {} } = this.props;

        if (isFreeproduct) {
            return (
                <p block="CartItem" elem="Price">
                    { this.renderFreeProductLabel() }
                </p>
            );
        }

        return super.renderProductPrice();
    }

    renderProductUnitPrice() {
        const {
            currency_code,
            item: {
                isFreeproduct,
                prices: {
                    price: {
                        value: price = 0
                    } = {}
                } = {}
            },
            isEditing,
            isCartOverlay,
            isMobileLayout
        } = this.props;

        if (isFreeproduct) {
            return (
                <p block="CartItem" elem="Price" mods={ { Unit: true } }>
                    { (!isEditing || isCartOverlay || isMobileLayout) && this.renderFreeProductLabel() }
                </p>
            );
        }

        return (
            <CartItemPrice
              row_total={ Number(price || 0).toFixed(2) }
              row_total_incl_tax={ price }
              currency_code={ currency_code }
              mix={ {
                  block: 'CartItem',
                  elem: 'Price',
                  mods: { isCartOverlay, isMobileLayout, Unit: true }
              } }
            />
        );
    }

    /**
     * Override: disable change of quantity for free products
     * + remove input quantity attribute to allow keyboard modification
     * + remove click and keydown listener preventing event bubbling
     * @returns {JSX.Element|*}
     */
    renderQuantityChangeField() {
        const {
            item: {
                sku,
                quantity,
                product: {
                    stock_item: {
                        qty_increments: qtyIncrement = 1
                    } = {}
                } = {},
                isFreeproduct
            } = {},
            minSaleQuantity,
            maxSaleQuantity,
            handleChangeQuantity,
            isProductInStock,
            isCartOverlay
        } = this.props;

        if (isFreeproduct) {
            return (
                <div block="CartItem" elem="QuantityWrapper">
                    <span>{ quantity }</span>
                </div>
            );
        }

        if (!isProductInStock) {
            return <div block="CartItem" elem="QuantityWrapper" mods={ { isPlaceholder: true } } />;
        }

        return (
            <div
              block="CartItem"
              elem="QuantityWrapper"
              mods={ { isCartOverlay } }
              // onClick={ this.quantityClickHandler }
              // onKeyDown={ this.quantityClickHandler }
              role="button"
              tabIndex="-1"
            >
                <Field
                  id="item_qty"
                  type={ FIELD_TYPE.numberWithControls }
                  attr={ {
                      id: `${sku}_item_qty`,
                      name: `${sku}_item_qty`,
                      defaultValue: quantity,
                      min: minSaleQuantity,
                      max: maxSaleQuantity,
                      step: qtyIncrement
                  } }
                  // value={ quantity }
                  events={ {
                      onChange: handleChangeQuantity
                  } }
                  validationRule={ {
                      inputType: VALIDATION_INPUT_TYPE.numeric,
                      range: {
                          min: minSaleQuantity,
                          max: maxSaleQuantity
                      }
                  } }
                  validateOn={ ['onChange'] }
                  mix={ { block: 'CartItem', elem: 'Qty' } }
                />
            </div>
        );
    }

    renderWishlistButton() {
        const { item: { product, isFreeproduct } = {}, onProductAddedToWishlist, isCartOverlay } = this.props;

        if (isFreeproduct || isCartOverlay) {
            return null;
        }

        return (
            <ProductWishlistButton
              magentoProduct={ magentoProductTransform(ADD_TO_WISHLIST, product) }
              // isInCart
              onProductAddedToWishlist={ onProductAddedToWishlist }
            />
        );
    }

    /**
     * Override: hide delete button for free products
     * @returns {null|*}
     */
    renderDeleteButton() {
        const { item: { isFreeproduct, isExtraProduct } = {} } = this.props;

        if (isFreeproduct && !isExtraProduct) {
            return null;
        }

        return super.renderDeleteButton();
    }

    /**
     * Override
     * @returns {JSX.Element}
     */
    renderMobileContent() {
        const {
            isMobileLayout,
            isProductInStock,
            item: {
                isFreeproduct
            } = {}
        } = this.props;

        return (
            <div
              block="CartItem"
              elem="Wrapper"
              mods={ { isMobileLayout, isProductOutOfStock: !isProductInStock, isFreeproduct } }
            >
                { this.renderImage() }
                <div block="CartItem" elem="CartItemRows">
                    <div block="CartItem" elem="ProductInfo" mods={ { isMobileLayout } }>
                        { this.renderTitle() }
                        { this.renderWishlistButton() }
                        { this.renderDeleteButton() }
                    </div>
                    <div block="CartItem" elem="ProductActions" mods={ { isMobileLayout } }>
                        { this.renderQuantityChangeField() }
                        { this.renderProductUnitPrice() }
                    </div>
                </div>
                { this.renderProductPickUpInfo() }
            </div>
        );
    }

    /**
     * Override: change layout, add unit price
     * @returns {JSX.Element}
     */
    renderDesktopContent() {
        const {
            item: {
                isFreeproduct
            } = {}
        } = this.props;

        return (
            <div block="CartItem" elem="Wrapper" mods={ { isCart: true, isFreeproduct } }>
                <div block="CartItem" elem="ProductInfo">
                    { this.renderImage() }
                    { this.renderTitle() }
                </div>
                { this.renderProductUnitPrice() }
                <div
                  block="CartItem"
                  elem="ProductQty"
                >
                    { this.renderQuantityChangeField() }
                </div>
                { this.renderProductPrice() }
                <div
                  block="CartItem"
                  elem="ProductActions"
                >
                    { this.renderWishlistButton() }
                    { this.renderDeleteButton() }
                </div>
                { this.renderProductPickUpInfo() }
            </div>
        );
    }

    renderBundleProductOptionValue(value, index) {
        const {
            label, quantity, id, sku
        } = value;

        return (
            <div key={ `${id}-${index}` }>
                { `${quantity} x ${label} - ${sku}` }
            </div>
        );
    }

    /**
     * Override: remove link
     * @returns {JSX.Element}
     */
    renderContent() {
        return (
            <span block="CartItem" elem="Link">
                { this.renderWrapperContent() }
            </span>
        );
    }

    /**
     * Override: hide if free product in rule
     * @returns {JSX.Element}
     */
    render() {
        const { isEditing, isCartOverlay, isChosenGiftProduct } = this.props;

        return (
            <div block="CartItem" mods={ { isEditing, isCartOverlay, isHidden: isChosenGiftProduct } }>
                { this.renderLoader() }
                { this.renderContent() }
            </div>
        );
    }
}

export default CartItemComponent;
